/**
 * Os 20 tipos de bloco — catálogo da paleta, valores iniciais, saídas
 * ("portas") e o resumo de uma linha que aparece no card. Espelha
 * `flows/domain/definition.py`.
 */
import { newId } from './ids';
import type { Block, BlockOf, BlockType, Branch, ChoiceItem, DocGroup, Rule, RuleOp } from './types';

export type BlockCategory = 'messages' | 'inputs' | 'logic' | 'actions';

export const CATEGORIES: { id: BlockCategory; label: string }[] = [
  { id: 'messages', label: 'Mensagens' },
  { id: 'inputs', label: 'Perguntas' },
  { id: 'logic', label: 'Lógica' },
  { id: 'actions', label: 'Ações' },
];

/** Nome do ícone — a apresentação troca pelo componente (o domínio não conhece React). */
export type BlockIcon =
  | 'text'
  | 'image'
  | 'video'
  | 'link'
  | 'choice'
  | 'input_text'
  | 'email'
  | 'phone'
  | 'number'
  | 'date'
  | 'rating'
  | 'condition'
  | 'set_variable'
  | 'jump'
  | 'wait'
  | 'end'
  | 'ai'
  | 'visit'
  | 'webhook'
  | 'open';

export interface BlockSpec {
  type: BlockType;
  label: string;
  /** Uma frase para a dica da paleta. */
  hint: string;
  category: BlockCategory;
  icon: BlockIcon;
}

export const BLOCKS: BlockSpec[] = [
  { type: 'text', label: 'Texto', hint: 'Uma mensagem da Centuria', category: 'messages', icon: 'text' },
  {
    type: 'image',
    label: 'Imagem',
    hint: 'Uma imagem por endereço https',
    category: 'messages',
    icon: 'image',
  },
  {
    type: 'video',
    label: 'Vídeo',
    hint: 'YouTube, Vimeo ou arquivo de vídeo',
    category: 'messages',
    icon: 'video',
  },
  {
    type: 'link',
    label: 'Link',
    hint: 'Um botão para um site ou página do portal',
    category: 'messages',
    icon: 'link',
  },
  { type: 'input_choice', label: 'Botões', hint: 'Escolha entre opções', category: 'inputs', icon: 'choice' },
  {
    type: 'input_text',
    label: 'Texto livre',
    hint: 'Resposta escrita',
    category: 'inputs',
    icon: 'input_text',
  },
  { type: 'input_email', label: 'E-mail', hint: 'Pede um e-mail válido', category: 'inputs', icon: 'email' },
  {
    type: 'input_phone',
    label: 'Telefone',
    hint: 'Telefone do Brasil, com DDD',
    category: 'inputs',
    icon: 'phone',
  },
  {
    type: 'input_number',
    label: 'Número',
    hint: 'Um número, com limites',
    category: 'inputs',
    icon: 'number',
  },
  { type: 'input_date', label: 'Data', hint: 'Uma data (dd/mm/aaaa)', category: 'inputs', icon: 'date' },
  { type: 'input_rating', label: 'Nota', hint: 'Nota de 1 a N', category: 'inputs', icon: 'rating' },
  {
    type: 'condition',
    label: 'Condição',
    hint: 'Segue um caminho conforme as respostas',
    category: 'logic',
    icon: 'condition',
  },
  {
    type: 'set_variable',
    label: 'Definir variável',
    hint: 'Guarda um valor',
    category: 'logic',
    icon: 'set_variable',
  },
  { type: 'jump', label: 'Pular para card', hint: 'Continua em outro card', category: 'logic', icon: 'jump' },
  { type: 'wait', label: 'Esperar', hint: 'Uma pausa de alguns segundos', category: 'logic', icon: 'wait' },
  { type: 'end', label: 'Encerrar', hint: 'Termina a conversa', category: 'logic', icon: 'end' },
  {
    type: 'ai_handoff',
    label: 'Conversar com a Centuria (IA)',
    hint: 'Encerra o fluxo e passa para o chat com a IA',
    category: 'actions',
    icon: 'ai',
  },
  {
    type: 'visit_request',
    label: 'Pedir visita',
    hint: 'Cria um pedido de visita',
    category: 'actions',
    icon: 'visit',
  },
  {
    type: 'webhook',
    label: 'Webhook',
    hint: 'Envia variáveis a um sistema externo',
    category: 'actions',
    icon: 'webhook',
  },
  {
    type: 'open',
    label: 'Abrir aula/página',
    hint: 'Um botão para uma aula ou página do portal',
    category: 'actions',
    icon: 'open',
  },
];

const SPEC = new Map(BLOCKS.map((b) => [b.type, b]));

export function blockSpec(type: BlockType): BlockSpec {
  return SPEC.get(type)!;
}

export function isBlockType(v: string): v is BlockType {
  return SPEC.has(v as BlockType);
}

// ───────────── valores iniciais ─────────────

export function newChoiceItem(taken: ReadonlySet<string>, label = ''): ChoiceItem {
  return { id: newId('i', taken), label, value: '' };
}

export function newRule(): Rule {
  return { variable: '', op: 'equals', value: '' };
}

export function newBranch(taken: ReadonlySet<string>): Branch {
  return { id: newId('c', taken), label: '', match: 'all', rules: [newRule()] };
}

const FACTORIES: { [T in BlockType]: (id: string) => BlockOf<T> } = {
  text: (id) => ({ type: 'text', id, text: '' }),
  image: (id) => ({ type: 'image', id, url: '', alt: '' }),
  video: (id) => ({ type: 'video', id, url: '' }),
  link: (id) => ({ type: 'link', id, label: '', url: '' }),
  input_choice: (id) => ({
    type: 'input_choice',
    id,
    variable: '',
    items: [newChoiceItem(new Set(), 'Opção 1')],
    multiple: false,
    button_label: '',
    retry_message: '',
  }),
  input_text: (id) => ({
    type: 'input_text',
    id,
    variable: '',
    placeholder: '',
    long: false,
    retry_message: '',
  }),
  input_email: (id) => ({ type: 'input_email', id, variable: '', placeholder: '', retry_message: '' }),
  input_phone: (id) => ({ type: 'input_phone', id, variable: '', placeholder: '', retry_message: '' }),
  input_number: (id) => ({
    type: 'input_number',
    id,
    variable: '',
    placeholder: '',
    min: null,
    max: null,
    integer: false,
    retry_message: '',
  }),
  input_date: (id) => ({
    type: 'input_date',
    id,
    variable: '',
    allow_past: true,
    allow_future: true,
    retry_message: '',
  }),
  input_rating: (id) => ({
    type: 'input_rating',
    id,
    variable: '',
    max: 5,
    low_label: '',
    high_label: '',
    retry_message: '',
  }),
  condition: (id) => ({ type: 'condition', id, branches: [newBranch(new Set())] }),
  set_variable: (id) => ({ type: 'set_variable', id, variable: '', value: '' }),
  jump: (id) => ({ type: 'jump', id, group_id: '' }),
  wait: (id) => ({ type: 'wait', id, seconds: 1 }),
  end: (id) => ({ type: 'end', id }),
  ai_handoff: (id) => ({ type: 'ai_handoff', id, question: '', instructions: '' }),
  visit_request: (id) => ({
    type: 'visit_request',
    id,
    phone_variable: '',
    availability_variables: [],
    topic: '',
    notes: '',
    result_variable: '',
  }),
  webhook: (id) => ({ type: 'webhook', id, url: '', variables: [], result_variable: '' }),
  open: (id) => ({ type: 'open', id, label: '', path: '', lesson_id: '', lesson_title: '' }),
};

/** Um bloco novo do tipo, com os valores iniciais e um id livre. */
export function createBlock<T extends BlockType>(type: T, taken?: ReadonlySet<string>): BlockOf<T> {
  return FACTORIES[type](newId('b', taken));
}

// ───────────── natureza dos blocos ─────────────

export const INPUT_TYPES: ReadonlySet<BlockType> = new Set([
  'input_choice',
  'input_text',
  'input_email',
  'input_phone',
  'input_number',
  'input_date',
  'input_rating',
]);

/** Depois destes nada do card roda e nenhuma ligação sai (`TERMINAL_TYPES`). */
export const TERMINAL_TYPES: ReadonlySet<BlockType> = new Set(['end', 'ai_handoff', 'jump']);

/** Integrações: têm a saída "error" (`FALLIBLE_TYPES`). */
export const FALLIBLE_TYPES: ReadonlySet<BlockType> = new Set(['webhook', 'visit_request']);

export const ERROR_PORT = 'error';

export type InputBlock = Extract<Block, { type: `input_${string}` }>;

export function isInput(block: Block): block is InputBlock {
  return INPUT_TYPES.has(block.type);
}

export function isTerminal(block: Block): boolean {
  return TERMINAL_TYPES.has(block.type);
}

export const itemsOf = (b: BlockOf<'input_choice'>): ChoiceItem[] => b.items ?? [];
export const branchesOf = (b: BlockOf<'condition'>): Branch[] => b.branches ?? [];
export const rulesOf = (b: Branch): Rule[] => b.rules ?? [];

// ───────────── saídas ─────────────

export type PortKind = 'item' | 'branch' | 'error' | 'next';

export interface Port {
  blockId: string;
  /** `null`: a saída padrão do bloco. */
  itemId: string | null;
  kind: PortKind;
  label: string;
}

/**
 * As saídas de um bloco: uma por opção (escolha única), uma por condição,
 * "error" nas integrações e a padrão — só no último bloco do card. Blocos
 * terminais não têm saída.
 */
export function blockPorts(block: Block, isLast: boolean): Port[] {
  if (isTerminal(block)) return [];
  const ports: Port[] = [];
  const port = (itemId: string | null, kind: PortKind, label: string) =>
    ports.push({ blockId: block.id, itemId, kind, label });

  if (block.type === 'input_choice' && !block.multiple) {
    itemsOf(block).forEach((item, i) => port(item.id, 'item', item.label.trim() || `Opção ${i + 1}`));
  } else if (block.type === 'condition') {
    branchesOf(block).forEach((b, i) => port(b.id, 'branch', branchLabel(b, i)));
  }
  if (FALLIBLE_TYPES.has(block.type)) port(ERROR_PORT, 'error', 'Se falhar');
  if (isLast) {
    const label =
      block.type === 'condition'
        ? 'Senão'
        : block.type === 'input_choice' && !block.multiple
          ? 'Demais respostas'
          : '';
    port(null, 'next', label);
  }
  return ports;
}

/** Saídas de todos os blocos do card, na ordem em que aparecem. */
export function groupPorts(group: DocGroup): Port[] {
  return group.blocks.flatMap((b, i) => blockPorts(b, i === group.blocks.length - 1));
}

/**
 * Id da alça de saída no canvas: `<bloco>:<opção>` (vazio depois dos dois
 * pontos é a saída padrão). Ids do documento nunca têm ":".
 */
export function portHandleId(blockId: string, itemId: string | null): string {
  return `${blockId}:${itemId ?? ''}`;
}

export function parsePortHandle(
  handle: string | null | undefined,
): { blockId: string; itemId: string | null } | null {
  if (!handle) return null;
  const at = handle.indexOf(':');
  if (at <= 0) return null;
  const itemId = handle.slice(at + 1);
  return { blockId: handle.slice(0, at), itemId: itemId || null };
}

// ───────────── resumo de uma linha ─────────────

export const OPERATORS: { value: RuleOp; label: string; valueless?: boolean }[] = [
  { value: 'equals', label: 'é igual a' },
  { value: 'not_equals', label: 'é diferente de' },
  { value: 'contains', label: 'contém' },
  { value: 'not_contains', label: 'não contém' },
  { value: 'greater_than', label: 'é maior que' },
  { value: 'less_than', label: 'é menor que' },
  { value: 'is_set', label: 'foi preenchida', valueless: true },
  { value: 'is_empty', label: 'está vazia', valueless: true },
];

const OP_LABEL = new Map(OPERATORS.map((o) => [o.value, o]));

export function isValueless(op: RuleOp): boolean {
  return !!OP_LABEL.get(op)?.valueless;
}

export function ruleText(rule: Rule): string {
  const op = OP_LABEL.get(rule.op);
  const name = rule.variable ? `{{${rule.variable}}}` : '(variável)';
  if (op?.valueless) return `${name} ${op.label}`;
  return `${name} ${op?.label ?? rule.op} "${rule.value}"`;
}

export function branchLabel(branch: Branch, index: number): string {
  if (branch.label.trim()) return branch.label.trim();
  const rules = rulesOf(branch);
  if (rules.length === 0) return `Condição ${index + 1}`;
  const joiner = branch.match === 'any' ? ' ou ' : ' e ';
  return rules.map(ruleText).join(joiner);
}

/** Texto sem a marcação (**negrito**, *itálico*, [link](url)), numa linha só. */
export function plainText(text: string): string {
  return text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function host(url: string): string {
  const m = /^[a-z]+:\/\/([^/?#]+)/i.exec(url.trim());
  return m ? m[1]! : url.trim();
}

const saves = (variable: string) => (variable ? ` → {{${variable}}}` : '');

/** O que o bloco faz, numa linha — o texto do bloco no card. */
export function blockSummary(block: Block, groups: readonly DocGroup[] = []): string {
  switch (block.type) {
    case 'text':
      return plainText(block.text) || 'Mensagem vazia';
    case 'image':
      return block.alt.trim() || (block.url ? host(block.url) : 'Sem imagem');
    case 'video':
      return block.url ? host(block.url) : 'Sem vídeo';
    case 'link':
      return block.label.trim() || block.url.trim() || 'Link sem texto';
    case 'input_choice': {
      const labels = itemsOf(block).map((i) => i.label.trim() || '…');
      const head = labels.length ? labels.join(' · ') : 'Sem opções';
      return `${block.multiple ? 'Várias: ' : ''}${head}${saves(block.variable)}`;
    }
    case 'input_text':
      return `${block.long ? 'Texto longo' : 'Texto'}${saves(block.variable)}`;
    case 'input_email':
      return `E-mail${saves(block.variable)}`;
    case 'input_phone':
      return `Telefone${saves(block.variable)}`;
    case 'input_number':
      return `Número${saves(block.variable)}`;
    case 'input_date':
      return `Data${saves(block.variable)}`;
    case 'input_rating':
      return `Nota de 1 a ${block.max}${saves(block.variable)}`;
    case 'condition': {
      const n = branchesOf(block).length;
      return n === 0 ? 'Sem condições' : n === 1 ? '1 condição' : `${n} condições`;
    }
    case 'set_variable':
      return block.variable ? `{{${block.variable}}} = ${block.value || '(vazio)'}` : 'Escolha a variável';
    case 'jump': {
      const target = groups.find((g) => g.id === block.group_id);
      return target ? `Vai para "${target.title || 'Card sem título'}"` : 'Escolha o card';
    }
    case 'wait':
      return `${String(block.seconds).replace('.', ',')} s`;
    case 'end':
      return 'Encerra a conversa';
    case 'ai_handoff':
      return plainText(block.question) || 'Passa para a IA';
    case 'visit_request':
      return block.topic.trim() || 'Pede uma visita';
    case 'webhook':
      return block.url ? host(block.url) : 'Sem endereço';
    case 'open': {
      const target = block.lesson_title || block.path;
      return [block.label.trim() || 'Botão sem texto', target].filter(Boolean).join(' → ');
    }
  }
}
